/**
 * MandateForm (Screen 1)
 * ─────────────────────
 * Shows mandate details + two tabs: "Send SMS Link" and "Generate QR".
 * Both tabs call POST /upi/register/autopay (no VPA) once to register the mandate.
 * QR tab: shows qrData from PhonePe. SMS tab: shows intentUrl for sharing.
 * User checks consent then taps "Proceed to Authorise" → UpiAuthorise polling.
 */
import React, { useState, useEffect, useRef } from "react";
import QRCode from "qrcode.react";
import AppBar from "./AppBar";
import MemberStrip from "./MemberStrip";
import { useMandate } from "./MandateContext";
import customFetch from "../apicall/api";
import {
  mandateFieldsFromResponse,
  findRegisteredVpaMandate,
  findInitiatedVpaMandateForVendor,
  contextFromRegisteredVpaMandate,
  contextFromInitiatedVpaMandate,
  isMandatePollingComplete,
  getMandateAuthUiMode,
} from "./mandateValidity";

const POLL_INTERVAL = 5000; // 5 seconds

export default function MandateForm({ onNext, onBack, api_token }) {
  const { state, update } = useMandate();
  api_token = api_token || localStorage.getItem("api_token");

  const [activeTab, setActiveTab]   = useState("qr");
  const [mobile,    setMobile]      = useState((state.selectedMember && state.selectedMember.mobileNo) || "");
  const [loading,   setLoading]     = useState(false);
  const [error,     setError]       = useState("");
  const [registered, setRegistered] = useState(!!state.redirectUrl);  // true if QR already generated (re-entry)
  const [pollState, setPollState]   = useState("idle");  // idle | waiting | checking | done

  const pollRef = useRef(null);

  // ── on mount: restore from vpaDetails if context is empty (e.g. fresh page load) ─
  useEffect(() => {
    if (registered) return;
    const vpaDetails = (state.selectedMember && state.selectedMember.vpaDetails) || [];

    const registeredMandate = findRegisteredVpaMandate(vpaDetails);
    if (registeredMandate) {
      update(contextFromRegisteredVpaMandate(registeredMandate));
      onNext();
      return;
    }

    const initiated = findInitiatedVpaMandateForVendor(
      vpaDetails,
      state.mapId,
      state.payment_vendor_code
    );
    if (initiated) {
      update(contextFromInitiatedVpaMandate(initiated));
      setRegistered(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── stop polling helper ───────────────────────────────────────────────────
  function stopPoll() {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }

  // ── start polling once QR is registered ──────────────────────────────────
  useEffect(() => {
    if (!registered || (!state.merchantUserId && !state.authRequestId)) return;

    const pollingKey = state.merchantUserId || state.authRequestId;
    if (!pollingKey) return;

    setPollState("waiting");
    pollRef.current = setInterval(() => {
      setPollState("checking");
      const params = new URLSearchParams({
        payment_vendor_code: state.payment_vendor_code,
      });
      if (state.householdId) {
        params.set("household_id", String(state.householdId));
      }
      if (state.selectedMember && state.selectedMember.family_member_id) {
        params.set("family_member_id", String(state.selectedMember.family_member_id));
      }
      if (state.payment_vendor_code === "RP") {
        if (state.authRequestId) {
          params.set("auth_request_id", state.authRequestId);
        } else if (state.merchantUserId) {
          params.set("merchant_user_id", state.merchantUserId);
        }
      } else if (state.merchantUserId) {
        params.set("merchant_user_id", state.merchantUserId);
      } else if (state.authRequestId) {
        params.set("auth_request_id", state.authRequestId);
      }
      const qp = params.toString();
      customFetch(
        process.env.REACT_APP_URL + "/upi/get/auth/request/status?" + qp,
        { method: "GET", headers: { "api-token": api_token } }
      )
        .then((res) => res.json())
        .then((data) => {
          if (data.api_code === 4) { localStorage.clear(); window.location.href = "/"; return; }
          const resp = Array.isArray(data) ? data[0] : data;
          const txState        = resp && resp.data && resp.data.transactionDetails && resp.data.transactionDetails.state;
          const payRespCode   = resp && resp.data && resp.data.transactionDetails && resp.data.transactionDetails.payResponseCode;
          const isCompleted = isMandatePollingComplete(resp, state.payment_vendor_code);
          if (isCompleted) {
            stopPoll();
            update({
              status: "success",
              ...mandateFieldsFromResponse(resp),
              authRequestId: state.authRequestId || resp.auth_request_id || "",
            });
            setPollState("done");
            onNext();
          } else if (txState === "FAILED") {
            stopPoll();
            // INTENT_EXPIRED / SUBSCRIPTION_EXPIRED means the QR timed out — don't
            // treat as a mandate failure; reset so the user can generate a fresh QR.
            const isExpiry = payRespCode === "INTENT_EXPIRED" || payRespCode === "SUBSCRIPTION_EXPIRED";
            if (isExpiry) {
              update({ redirectUrl: "", redirectType: "", merchantUserId: "", authRequestId: "", mandateRef: "" });
              setRegistered(false);
              setPollState("idle");
              setError("QR code expired. Please generate a new one.");
            } else {
              update({ status: "failed" });
              setPollState("done");
              onNext();
            }
          } else {
            setPollState("waiting");
          }
        })
        .catch(() => setPollState("waiting"));
    }, POLL_INTERVAL);

    return () => stopPoll();
  }, [registered, state.merchantUserId, state.authRequestId, state.payment_vendor_code]); // eslint-disable-line react-hooks/exhaustive-deps

  const memberName  = (state.selectedMember && state.selectedMember.memberName) || "";
  const maxAmount   = state.maxBillingAmount || 0;

  // ── call /upi/register/autopay (no VPA) → get redirect_type + redirect_url ──
  function registerMandate() {
    if (loading) return;
    setLoading(true);
    setError("");

    const formdata = new FormData();
    formdata.append("household_id",     state.householdId);
    formdata.append("family_member_id", state.selectedMember && state.selectedMember.family_member_id);
    formdata.append("payment_vendor_code", state.payment_vendor_code || "PP");
    if (state.mapId != null && state.mapId !== "") {
      formdata.append("map_id", String(state.mapId));
    }

    customFetch(process.env.REACT_APP_URL + "/upi/register/autopay", {
      method: "POST",
      headers: { "api-token": api_token },
      body: formdata,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.api_code === 4) { localStorage.clear(); window.location.href = "/"; return; }
        const resp = Array.isArray(data) ? data[0] : data;
        if (!resp || resp.status !== 1) {
          setError(resp.message || "Registration failed. Please try again.");
          setLoading(false);
          return;
        }
        // Mandate already REGISTERED — skip QR, navigate to confirmation
        if (resp.already_registered) {
          update({
            status: "success",
            mandateRef:    resp.subscription_id   || "",
            merchantUserId: resp.merchant_user_id || "",
            ...mandateFieldsFromResponse(resp),
          });
          setLoading(false);
          onNext();
          return;
        }
        update({
          redirectType:    resp.redirect_type  || "",
          redirectUrl:     resp.redirect_url   || "",
          merchantUserId:  resp.merchant_user_id || "",
          authRequestId:   resp.auth_request_id  || "",
          mandateRef:      resp.subscription_id  || "",
          maxBillingAmount: resp.max_billing_amount || state.maxBillingAmount,
          ...mandateFieldsFromResponse(resp),
        });
        setRegistered(true);
        setLoading(false);
      })
      .catch(() => {
        setError("Network error. Please try again.");
        setLoading(false);
      });
  }

  const qrValue = state.redirectUrl || "";
  const authUiMode = getMandateAuthUiMode(
    state.payment_vendor_code,
    qrValue,
    state.redirectType
  );
  const showQrImage = authUiMode === "qr_image";
  const showQrCode = authUiMode === "qr_code" && !!qrValue;

  // ── shared styles ──────────────────────────────────────────────────────────
  const tabActive   = { background: "#0178B7", color: "#fff", border: "none", borderRadius: 8, padding: "8px 0", flex: 1, fontWeight: 700, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 };
  const tabInactive = { background: "transparent", color: "#6b7280", border: "none", borderRadius: 8, padding: "8px 0", flex: 1, fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 };

  return (
    <section className="main-sec" style={{ minHeight: "100vh" }}>
      <div className="container fix-width">
      <AppBar title="Mandate Details" onBack={onBack} />
      <MemberStrip memberName={memberName} partnerName={state.partnerName || state.paymentVendorName} />

      <div style={{ padding: "20px 0 20px" }}>

        {/* Merchant card */}
        {/* <div style={{ borderRadius: 16, marginBottom: 16, background: "#fff", boxShadow: "0 1px 6px rgba(0,0,0,0.08)", border: "1px solid #f0f0f0" }}>
          <div style={{ padding: "10px 16px", display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#166534", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 18, flexShrink: 0 }}>N</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Navadhan</div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>Monthly Loan Repayment</div>
            </div>
          </div>
        </div> */}

        {/* Mandate terms card */}
        {/* <div style={{ background: "#f5f0ff", border: "1px solid #e9d5ff", borderRadius: 16, padding: "14px 16px", marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
            <span style={{ fontSize: 12, color: "#6b7280" }}>Maximum Debit Amount</span>
            <span style={{ fontWeight: 700, fontSize: 22, color: "#0178B7" }}>
              ₹{maxAmount.toLocaleString("en-IN")}.00{" "}
              <span title="Actual debit ≤ this amount" style={{ fontSize: 13, color: "#9ca3af", cursor: "help" }}>ⓘ</span>
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", rowGap: 8, fontSize: 12 }}>
            {[
              ["Frequency",  "Monthly"],
              ["Debit Type", "BEFORE_DUE_DATE"],
              ["Start Date", fmtDate(today)],
              ["End Date",   fmtDate(endDate)],
            ].map(([label, val]) => (
              <div key={label}>
                <div style={{ color: "#6b7280", fontSize: 11 }}>{label}</div>
                <div style={{ fontWeight: 600 }}>{val}</div>
              </div>
            ))}
          </div>
        </div> */}

        {/* ── Tab toggle (SMS tab disabled — QR only) ── */}
        {/* <div style={{ display: "flex", background: "#f3f4f6", borderRadius: 10, padding: 4, marginBottom: 14 }}>
          <button style={activeTab === "sms" ? tabActive : tabInactive} onClick={() => setActiveTab("sms")}>
            <span style={{ fontSize: 15 }}>📱</span> Send SMS Link
          </button>
          <button style={activeTab === "qr" ? tabActive : tabInactive} onClick={() => setActiveTab("qr")}>
            <span style={{ fontSize: 15 }}>⊞</span> Generate QR
          </button>
        </div> */}

        {/* ── SMS tab (commented out — QR only flow) ── */}
        {/* {activeTab === "sms" && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "#374151", letterSpacing: "0.05em", marginBottom: 8 }}>
              Enter Customer's Mobile Number
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: 8, padding: "0 10px", fontWeight: 600, fontSize: 14, flexShrink: 0 }}>
                +91
              </div>
              <input type="tel" placeholder="9876543210" value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                disabled={loading}
                style={{ borderRadius: 8, fontSize: 15, flex: 1, border: "1px solid #ced4da", padding: "8px 12px", outline: "none", background: "#fff", minWidth: 0 }}
              />
              <button
                style={{ background: "#0178B7", color: "#fff", borderRadius: 8, border: "none", whiteSpace: "nowrap", minWidth: 72, display: "flex", alignItems: "center", gap: 4, fontWeight: 700, padding: "8px 12px", cursor: loading || mobile.length < 10 ? "not-allowed" : "pointer" }}
                disabled={loading || mobile.length < 10} onClick={registerMandate}
              >
                {loading ? <span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid #fff", borderRightColor: "transparent", borderRadius: "50%", animation: "upi-spin 0.75s linear infinite" }} /> : <><span style={{ fontSize: 14 }}>✈</span> Send</>}
              </button>
            </div>
            {registered && (
              <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#166534", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 16 }}>✅</span> Link sent to +91 {mobile}.
              </div>
            )}
          </div>
        )} */}

        {/* ── QR (only active tab) ── */}
        <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "#374151", letterSpacing: "0.05em", marginBottom: 6 }}>
              Generate QR for Mandate Authorisation
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
              The customer scans this QR code with any UPI app to approve the mandate.
            </div>

            {!registered && (
              <button
                style={{ background: "#0178B7", color: "#fff", borderRadius: 8, border: "none", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", fontWeight: 700, marginBottom: 12, padding: "12px 0", cursor: loading ? "not-allowed" : "pointer" }}
                disabled={loading}
                onClick={registerMandate}
              >
                {loading
                  ? <><span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid #fff", borderRightColor: "transparent", borderRadius: "50%", animation: "upi-spin 0.75s linear infinite", marginRight: 8 }} />Generating…</>
                  : <><span style={{ fontSize: 16 }}>⊞</span> Generate QR Code</>}
              </button>
            )}

            {error && (
              <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#dc2626", marginBottom: 10 }}>
                {error}
                <button style={{ background: "#dc2626", color: "#fff", borderRadius: 6, border: "none", padding: "2px 8px", marginLeft: 8, cursor: "pointer", fontSize: 12 }} onClick={registerMandate}>Retry</button>
              </div>
            )}

            {registered && qrValue && (
              <div style={{ textAlign: "center", marginBottom: 14 }}>
                <div style={{ display: "inline-block", padding: 12, background: "#fff", border: "2px solid #0178B7", borderRadius: 12, marginBottom: 8 }}>
                  {showQrImage && (
                    <img src={qrValue} alt="UPI Mandate QR" style={{ width: 180, height: 180, display: "block" }} />
                  )}
                  {showQrCode && (
                    <QRCode value={qrValue} size={180} />
                  )}
                </div>
                <div style={{ fontSize: 12, color: "#166534", marginTop: 4, display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                  <span>✅</span> QR code generated successfully
                </div>
                {/* Polling status */}
                <div style={{ marginTop: 8, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontSize: 12, color: "#6b7280" }}>
                  {pollState === "checking"
                    ? <><span style={{ display: "inline-block", width: 12, height: 12, border: "2px solid #0178B7", borderRightColor: "transparent", borderRadius: "50%", animation: "upi-spin 0.75s linear infinite" }} /> Checking status…</>
                    : <><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#d97706" }} /> Waiting for customer approval…</>}
                </div>
                <button
                  style={{ background: "transparent", color: "#0178B7", border: "1.5px solid #0178B7", borderRadius: 20, fontSize: 12, padding: "4px 12px", cursor: "pointer", marginTop: 8 }}
                  onClick={() => { stopPoll(); setRegistered(false); setPollState("idle"); registerMandate(); }}
                >
                  Regenerate QR
                </button>
              </div>
            )}

            <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10, padding: "10px 12px", marginBottom: 14 }}>
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ fontSize: 12, color: "#1e40af" }}>
                  Ask the customer to open any UPI app, scan this QR, and enter their UPI PIN to approve the recurring mandate.
                </div>
              </div>
            </div>
        </div>


      </div>

      <style>{`
        @keyframes upi-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
      </div>
    </section>
  );
}
